import { Component } from '@theme/component'
import {
  animateDialogClose,
  animateDialogOpen,
  cancelDialogAnimations
} from '@theme/dialog-animation'
import { scrollLock } from '@theme/events'
import { removeTrapFocus } from '@theme/focus'
import { debounce, isClickedOutside, isPointWithinElement } from '@theme/utils'

const hasInvoker = 'command' in HTMLButtonElement.prototype

export class useDialog extends Component {
  requiredRefs = ['dialog']

  isAnimating = false

  #isNormalizingSide = false

  static get observedAttributes() {
    return ['side']
  }

  attributeChangedCallback(name, _old, value) {
    if (name === 'side') {
      this._updateSideClasses(value)
    }
  }

  connectedCallback() {
    this.#prepareOverlay()

    super.connectedCallback()

    this.panel =
      this.querySelector(
        '.drawer-panel, [data-drawer-panel], [role="navigation"], [role="search"], [role="dialog"]'
      ) ?? this.refs.dialog

    const drawerId = this.getAttribute('id') || `drawer-${Date.now()}`
    const overlayId = this.refs.dialog?.id || `${drawerId}-overlay`

    if (!this.hasAttribute('id')) {
      this.setAttribute('id', drawerId)
    }
    if (this.refs.dialog && !this.refs.dialog.id) {
      this.refs.dialog.id = overlayId
    }

    this._overlayId = overlayId

    this.#assignToggleButtons()
    this.#bindDialogEvents()
    this.#bindInvokerEvents()

    this.dataset.nav = this.dataset.nav || 'closed'
    this.inert = this.dataset.nav !== 'open'
    if (!this.hasAttribute('side')) {
      const initialSide = this.dataset.side
      this.setAttribute('side', initialSide || 'left')
    }
    if (!this.hasAttribute('type')) {
      this.setAttribute('type', this._autoDetectType())
    }
    this._updateSideClasses()
    this._setAriaExpanded(this.dataset.nav === 'open')

    if (this.hasActiveWidthRange) {
      window.addEventListener('resize', this.#handleResize)
    }
  }

  disconnectedCallback() {
    if (this.allToggleBtns?.length) {
      for (const btn of this.allToggleBtns) {
        btn.removeEventListener('click', this.toggleHandler)
      }
    }

    this.#unbindDialogEvents()
    this.#unbindInvokerEvents()

    if (this._addedDialogRef && this.refs.dialog) {
      this.refs.dialog.removeAttribute('ref')
    }

    if (this.hasActiveWidthRange) {
      window.removeEventListener('resize', this.#handleResize)
    }

    super.disconnectedCallback()
  }

  #handleResize = debounce(() => {
    if (!this.hasActiveWidthRange) {
      return
    }

    if (this.#isOutsideActiveWidthRange(window.innerWidth)) {
      void this.closeDialog()
    }
  }, 50)

  #isOutsideActiveWidthRange(windowWidth) {
    if (this.minWidth !== null && windowWidth <= this.minWidth) {
      return true
    }

    if (this.maxWidth !== null && windowWidth > this.maxWidth) {
      return true
    }

    return false
  }

  async showDialog() {
    const { dialog } = this.refs

    if (!dialog || dialog.open || this.isAnimating) {
      return
    }

    this.isAnimating = true
    this.removeAttribute('closing')
    this.inert = false
    this._setAriaExpanded(true)
    this.dataset.nav = 'open'
    cancelDialogAnimations(this.panel)

    dialog.showModal()
    this.#setDialogLock(true)

    queueMicrotask(() => {
      this.addEventListener('click', this.handleClick)
      this.addEventListener('keydown', this.handleKeyDown)
    })

    await animateDialogOpen(this, dialog)
    this.dispatchDialogEvent('dialog:open')
  }

  #bindDialogEvents() {
    if (this.refs.dialog) {
      this.refs.dialog.removeEventListener('cancel', this.#handleDialogCancel)
      this.refs.dialog.addEventListener('cancel', this.#handleDialogCancel)
    }
  }

  #unbindDialogEvents() {
    if (this.refs.dialog) {
      this.refs.dialog.removeEventListener('cancel', this.#handleDialogCancel)
    }
  }

  #handleDialogCancel = (event) => {
    event.preventDefault()
    void this.closeDialog()
  }

  #bindInvokerEvents() {
    if (!(hasInvoker && this.refs.dialog)) {
      return
    }

    this.refs.dialog.addEventListener('command', this.#handleCommand)
    this.refs.dialog.addEventListener('toggle', this.#handleToggle)
  }

  #unbindInvokerEvents() {
    if (!(hasInvoker && this.refs.dialog)) {
      return
    }

    this.refs.dialog.removeEventListener('command', this.#handleCommand)
    this.refs.dialog.removeEventListener('toggle', this.#handleToggle)
  }

  #handleCommand = (event) => {
    if (event.command === 'show-modal') {
      event.preventDefault()
      void this.showDialog()
      return
    }

    if (event.command === 'close' || event.command === 'request-close') {
      event.preventDefault()
      void this.closeDialog()
    }
  }

  #handleToggle = (event) => {
    this._setAriaExpanded(event.newState === 'open')
  }

  closeDialog = async () => {
    const { dialog } = this.refs

    if (!dialog?.open || this.isAnimating || this.isLockedOpen) {
      return
    }

    this.removeEventListener('click', this.handleClick)
    this.removeEventListener('keydown', this.handleKeyDown)

    this.isAnimating = true
    this.setAttribute('closing', '')
    this._setAriaExpanded(false)
    removeTrapFocus()

    await animateDialogClose(this, dialog, () => this.#setDialogLock(false))
    this.dispatchDialogEvent('dialog:close')
  }

  toggleDialog = () => {
    if (this.refs.dialog?.open || this.dataset.nav === 'open') {
      if (this.isLockedOpen) {
        return
      }
      void this.closeDialog()
    } else {
      void this.showDialog()
    }
  }

  toggle() {
    this.toggleDialog()
  }

  open() {
    void this.showDialog()
  }

  close() {
    void this.closeDialog()
  }

  handleClick(event) {
    const { dialog } = this.refs

    if (this.isLockedOpen || !dialog) {
      return
    }

    const clickedOutsideDialog = isClickedOutside(event, dialog)
    const clickedOverlay =
      !clickedOutsideDialog &&
      dialog.hasAttribute('data-overlay') &&
      this.panel &&
      !isPointWithinElement(event.clientX, event.clientY, this.panel)

    if (clickedOutsideDialog || clickedOverlay) {
      void this.closeDialog()
    }
  }

  handleKeyDown(event) {
    if (this.isLockedOpen) {
      return
    }

    if (event.key !== 'Escape') {
      return
    }

    event.preventDefault()
    void this.closeDialog()
  }

  get lockScroll() {
    return this.getAttribute('lock-scroll') !== 'false'
  }

  get hasActiveWidthRange() {
    return this.minWidth !== null || this.maxWidth !== null
  }

  get minWidth() {
    const value = this.getAttribute('dialog-active-min-width')
    return value === null ? null : Number(value)
  }

  get maxWidth() {
    const value = this.getAttribute('dialog-active-max-width')
    return value === null ? null : Number(value)
  }

  get isLockedOpen() {
    const shouldLock = this.dataset.lockUntilLogin === 'true'
    if (!shouldLock) {
      return false
    }

    const isCustomerLogged =
      this.dataset.customerLoggedIn === 'true' ||
      Boolean(
        globalThis.Shopify?.customer || globalThis.Shopify?.customerLoggedIn
      )

    return shouldLock && !isCustomerLogged
  }

  #setDialogLock(isOpen) {
    if (!this.lockScroll) {
      return
    }

    scrollLock(isOpen, 'dialog')
  }

  dispatchDialogEvent(eventName) {
    this.dispatchEvent(
      new CustomEvent(eventName, {
        bubbles: true,
        detail: { dialog: this.refs.dialog }
      })
    )
  }

  _setAriaExpanded(isOpen) {
    if (!this.allToggleBtns?.length) {
      return
    }

    const expanded = isOpen ? 'true' : 'false'
    for (const btn of this.allToggleBtns ?? []) {
      try {
        btn.setAttribute('aria-expanded', expanded)
        // biome-ignore lint/suspicious/noEmptyBlockStatements: intentional no-op
      } catch {}
    }
  }

  _autoDetectType() {
    if (this.hasAttribute('type')) {
      return this.getAttribute('type')
    }

    if (this.panel) {
      if (
        this.panel.matches('[role="search"]') ||
        this.panel.querySelector('input[type="search"]')
      ) {
        return 'search'
      }
      if (
        this.panel.matches('[role="navigation"]') ||
        this.panel.querySelector('nav, ul li a')
      ) {
        return 'menu'
      }
      if (this.panel.matches('[role="dialog"]')) {
        return 'dialog'
      }
    }

    return 'default'
  }

  _updateSideClasses(value) {
    const raw = value ?? this.getAttribute('side') ?? this.dataset.side
    const normalized = raw === 'right' ? 'right' : 'left'

    if (raw !== normalized) {
      if (this.#isNormalizingSide) {
        return
      }

      this.#isNormalizingSide = true
      this.setAttribute('side', normalized)
      this.#isNormalizingSide = false
      return
    }

    this._applySideState(normalized)
  }

  _applySideState(side) {
    this.dataset.side = side
    if (this.panel) {
      this.panel.dataset.side = side
    }
    if (this.overlay) {
      this.overlay.dataset.side = side
      this.overlay.setAttribute('side', side)
    }
  }

  #assignToggleButtons() {
    const drawerId = this.getAttribute('id') || ''
    const overlayId = this._overlayId || this.refs.dialog?.id || ''

    const internalToggleBtns = Array.from(
      this.querySelectorAll('button, [role="button"], [data-toggle]')
    ).filter(
      (btn) => !btn.closest('.drawer-dialog, [data-overlay], .drawer-overlay')
    )
    this.toggleBtns = internalToggleBtns

    const externalToggleBtns = overlayId
      ? Array.from(
          document.querySelectorAll(
            `button[aria-controls="${overlayId}"], [data-toggle="${drawerId}"], [data-drawer-toggle="${drawerId}"], [commandfor="${overlayId}"]`
          )
        ).filter((btn) => !this.contains(btn))
      : []
    this.externalToggleBtns = externalToggleBtns

    this.allToggleBtns = internalToggleBtns.concat(externalToggleBtns)

    this.toggleHandler = (ev) => {
      ev.preventDefault()
      this.toggle()
    }

    for (const btn of this.allToggleBtns) {
      btn.removeEventListener('click', this.toggleHandler)
      if (hasInvoker && btn.hasAttribute('commandfor')) continue
      btn.addEventListener('click', this.toggleHandler)
    }

    this._setAriaExpanded(this.dataset.nav === 'open')
  }

  #prepareOverlay() {
    this.panel = undefined
    this.overlay =
      this.querySelector('.drawer-dialog') ?? this.refs.dialog ?? undefined

    if (this.overlay) {
      this.overlay.setAttribute('data-lenis-prevent', '')
      if (!this.overlay.hasAttribute('ref')) {
        this.overlay.setAttribute('ref', 'dialog')
        this._addedDialogRef = true
      }
    }
  }
}

if (!customElements.get('use-dialog')) {
  customElements.define('use-dialog', useDialog)
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDialogElement) {
      const anyOpen = Boolean(
        document.querySelector('dialog[open]:not([lock-scroll="false"])')
      )
      scrollLock(anyOpen, 'dialog')
    }
  },
  { capture: true }
)
